// ============================================================
// Shared media-localisation: ElevenLabs voice catalogue.
//
// Extracted from apps/tours/services/agentGuide.ts so 360Tours and
// Rehub Record share ONE curated voice list and grouping logic.
// Tours re-exports these as AGENT_VOICE_LIBRARY etc.
// ============================================================

import { MEDIA_LANGUAGES, languageLabelFor } from './languages';

export type MediaVoiceProvider = 'elevenlabs' | 'falai';

export interface MediaVoiceOption {
    id: string;
    label: string;
    provider: MediaVoiceProvider;
    /** Group label used for the <optgroup> in the picker (the voice's native accent). */
    group: string;
    /** Optional one-line tone description shown next to the voice in the picker. */
    description?: string;
    /** Optional pre-recorded sample (≤ 5s) for a "listen before generating" UX. */
    sample_url?: string;
    /**
     * Present when the voice comes from the ElevenLabs Voice Library and may
     * need to be added to the project account on first use (see the
     * generate-narration edge functions). Premade / already-saved voices omit this.
     */
    public_owner_id?: string | null;
}

// Curated subset of ElevenLabs' *current* stable default voices (the legacy
// premade set retires 2026-12-31). Every voice is multilingual via the
// `eleven_flash_v2_5` model, so the accent below is the voice's native
// character — pair any of them with a language from MEDIA_LANGUAGES to
// narrate in that language.
export const MEDIA_VOICE_LIBRARY: MediaVoiceOption[] = [
    // --- English (UK) ---
    { id: 'JBFqnCBsd6RMkjVDRZzb', label: 'George — warm British male', provider: 'elevenlabs', group: 'English (UK)', description: 'Confident, friendly, ideal for residential tours.' },
    { id: 'onwK4e9ZLuTAKqWW03F9', label: 'Daniel — authoritative British male', provider: 'elevenlabs', group: 'English (UK)', description: 'Polished newsreader tone, suits prime / new-build.' },
    { id: 'pFZP5JQG7iQjIQuC4Bku', label: 'Lily — warm British female', provider: 'elevenlabs', group: 'English (UK)', description: 'Soft and welcoming, great for family homes.' },
    { id: 'Xb7hH8MSUJpSbSDYk0k2', label: 'Alice — confident British female', provider: 'elevenlabs', group: 'English (UK)', description: 'Clear and professional, broadly versatile.' },
    { id: 'XB0fDUnXU5powFXDhCwa', label: 'Charlotte — refined female', provider: 'elevenlabs', group: 'English (UK)', description: 'Soft, premium, country / luxury homes.' },
    // --- English (US) ---
    { id: 'TX3LPaxmHKxFdv7VOQHJ', label: 'Liam — relaxed American male', provider: 'elevenlabs', group: 'English (US)', description: 'Laid-back, suits boutique / lifestyle tours.' },
    { id: 'nPczCjzI2devNBz1zQrb', label: 'Brian — deep American male', provider: 'elevenlabs', group: 'English (US)', description: 'Warm, reassuring, premium feel.' },
    { id: 'cjVigY5qzO86Huf0OWal', label: 'Eric — smooth American male', provider: 'elevenlabs', group: 'English (US)', description: 'Smooth and modern, lifestyle listings.' },
    { id: 'iP95p4xoKVk53GoZ742B', label: 'Chris — casual American male', provider: 'elevenlabs', group: 'English (US)', description: 'Friendly and natural, first-time buyers.' },
    { id: '9BWtsMINqrJLrRacOk9x', label: 'Aria — expressive American female', provider: 'elevenlabs', group: 'English (US)', description: 'Bright and engaging, lifestyle tours.' },
    { id: 'FGY2WhTYpPnrIDTdsKH5', label: 'Laura — upbeat American female', provider: 'elevenlabs', group: 'English (US)', description: 'Sunny and energetic, broad appeal.' },
    { id: 'cgSgspJ2msm6clMCkdW9', label: 'Jessica — youthful American female', provider: 'elevenlabs', group: 'English (US)', description: 'Modern and friendly, urban apartments.' },
    { id: 'pNInz6obpgDQGcFmaJgB', label: 'Adam — neutral American male', provider: 'elevenlabs', group: 'English (US)', description: 'Neutral, professional, broadly versatile.' },
    { id: 'EXAVITQu4vr4xnSDxMaL', label: 'Sarah — soft American female', provider: 'elevenlabs', group: 'English (US)', description: 'Gentle and clear, great for calm walkthroughs.' },
    { id: 'XrExE9yKIg1WjnnlVkGX', label: 'Matilda — warm American female', provider: 'elevenlabs', group: 'English (US)', description: 'Warm narrator tone, family homes and schools.' },
    { id: 'bIHbv24MWmeRgasZH58o', label: 'Will — chill American male', provider: 'elevenlabs', group: 'English (US)', description: 'Relaxed conversational tone, lifestyle listings.' },
    { id: 'pqHfZKP75CvOlQylNhV4', label: 'Bill — crisp American male', provider: 'elevenlabs', group: 'English (US)', description: 'Documentary-style clarity, commercial / mixed-use.' },
    { id: 'CwhRBWXzGAHq8TQ4Fs17', label: 'Roger — easygoing American male', provider: 'elevenlabs', group: 'English (US)', description: 'Laid-back and approachable, broad appeal.' },
    // --- English (Australia) ---
    { id: 'IKne3meq5aSn9XLyUdCD', label: 'Charlie — natural Australian male', provider: 'elevenlabs', group: 'English (Australia)', description: 'Easy-going Aussie tone, lifestyle / coastal.' },
    // --- Other accents ---
    { id: 'N2lVS1w4EtoT3dr4eOWO', label: 'Callum — intense transatlantic male', provider: 'elevenlabs', group: 'Other accents', description: 'Characterful transatlantic voice, distinctive homes.' },
    { id: 'SAz9YHcvj6GT2YYXdXww', label: 'River — calm American (non-binary)', provider: 'elevenlabs', group: 'Other accents', description: 'Calm and neutral, inclusive option.' },
];

export const DEFAULT_VOICE_ID = MEDIA_VOICE_LIBRARY[0].id;

const ACCENT_GROUP: Record<string, string> = {
    british: 'English (UK)',
    english: 'English (UK)',
    'british rp': 'English (UK)',
    london: 'English (UK)',
    american: 'English (US)',
    us: 'English (US)',
    'us southern': 'English (US)',
    'standard american': 'English (US)',
    australian: 'English (Australia)',
    aussie: 'English (Australia)',
    irish: 'English (Ireland)',
    scottish: 'English (Scotland)',
    indian: 'English (India)',
    'south african': 'English (South Africa)',
    canadian: 'English (Canada)',
    'new zealand': 'English (New Zealand)',
    'new zealander': 'English (New Zealand)',
    kiwi: 'English (New Zealand)',
    swedish: 'Swedish',
    spanish: 'Spanish',
    italian: 'Italian',
    french: 'French',
    german: 'German',
    portuguese: 'Portuguese',
    dutch: 'Dutch',
    polish: 'Polish',
    transatlantic: 'Other accents',
};

/** Preferred optgroup order in the voice picker. */
export const VOICE_GROUP_ORDER = [
    'English (UK)',
    'English (US)',
    'English (Ireland)',
    'English (Scotland)',
    'English (Australia)',
    'English (Canada)',
    'English (New Zealand)',
    'English (India)',
    'English (South Africa)',
    'Other accents',
    'Spanish',
    'Italian',
    'French',
    'German',
    'Portuguese',
    'Dutch',
    'Polish',
    'Swedish',
    'Danish',
    'Turkish',
    'Greek',
    'Arabic',
    'Hindi',
    'Japanese',
    'Korean',
    'Chinese (Mandarin)',
    'Russian',
];

export const titleCase = (s: string): string =>
    s
        .split(/[\s_-]+/)
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(' ');

const languageDisplay = (codeOrName: string): string => {
    const c = codeOrName.toLowerCase();
    const byCode = MEDIA_LANGUAGES.find((l) => l.code === c);
    if (byCode) return byCode.label;
    const byLabel = MEDIA_LANGUAGES.find((l) => l.label.toLowerCase() === c);
    if (byLabel) return byLabel.label;
    return titleCase(codeOrName);
};

const isEnglish = (s: string | null | undefined): boolean => {
    const c = (s ?? '').toLowerCase();
    return (
        c === '' ||
        c === 'en' ||
        c === 'en-us' ||
        c === 'en-gb' ||
        c === 'en-au' ||
        c === 'en-ie' ||
        c === 'en-in' ||
        c === 'en-za' ||
        c === 'en-ca' ||
        c === 'en-nz' ||
        c === 'english'
    );
};

export const deriveVoiceGroup = (
    accent: string | null,
    language: string | null,
    languages: string[],
): string => {
    if (language && !isEnglish(language)) return languageDisplay(language);
    if (accent) {
        const a = accent.toLowerCase().trim();
        if (ACCENT_GROUP[a]) return ACCENT_GROUP[a];
        // Non-English accent labels that match a language group (e.g. "spanish")
        const asLanguage = languageDisplay(a);
        if (MEDIA_LANGUAGES.some((l) => l.label === asLanguage) && asLanguage !== 'English') {
            return asLanguage;
        }
        return `English (${titleCase(accent)})`;
    }
    const nonEnglish = (languages || []).find((l) => !isEnglish(l));
    if (nonEnglish) return languageDisplay(nonEnglish);
    return 'English';
};

/**
 * Merge curated defaults with live account/library voices. Curated entries
 * win on ID collision so labels stay consistent; library-only accents and
 * native-language voices append after.
 */
export const mergeVoiceCatalogues = (
    curated: MediaVoiceOption[],
    live: MediaVoiceOption[],
): MediaVoiceOption[] => {
    const byId = new Map<string, MediaVoiceOption>();
    for (const v of curated) byId.set(v.id, v);
    for (const v of live) {
        const existing = byId.get(v.id);
        if (!existing) {
            byId.set(v.id, v);
            continue;
        }
        // Keep curated labels/groups, but adopt public_owner_id if the live
        // payload has one (needed for first-use library add).
        if (v.public_owner_id && !existing.public_owner_id) {
            byId.set(v.id, { ...existing, public_owner_id: v.public_owner_id });
        }
    }
    const merged = [...byId.values()];
    const rank = (group: string) => {
        const i = VOICE_GROUP_ORDER.indexOf(group);
        return i === -1 ? VOICE_GROUP_ORDER.length + group.localeCompare('') : i;
    };
    merged.sort((a, b) => {
        const g = rank(a.group) - rank(b.group);
        if (g !== 0) return g;
        return a.label.localeCompare(b.label);
    });
    return merged;
};

/** Group any voice list by its `group` label, preserving preferred order. */
export const groupVoices = (
    list: MediaVoiceOption[],
): { group: string; voices: MediaVoiceOption[] }[] => {
    const order: string[] = [];
    const byGroup = new Map<string, MediaVoiceOption[]>();
    for (const v of list) {
        if (!byGroup.has(v.group)) {
            byGroup.set(v.group, []);
            order.push(v.group);
        }
        byGroup.get(v.group)!.push(v);
    }
    order.sort((a, b) => {
        const ia = VOICE_GROUP_ORDER.indexOf(a);
        const ib = VOICE_GROUP_ORDER.indexOf(b);
        const ra = ia === -1 ? VOICE_GROUP_ORDER.length : ia;
        const rb = ib === -1 ? VOICE_GROUP_ORDER.length : ib;
        if (ra !== rb) return ra - rb;
        return a.localeCompare(b);
    });
    return order.map((group) => ({ group, voices: byGroup.get(group)! }));
};

/** A voice group whose name matches the language label, when available. */
export const preferredVoicesForLanguage = (
    list: MediaVoiceOption[],
    languageCode: string,
): MediaVoiceOption[] => {
    const label = languageLabelFor(languageCode);
    return list.filter((v) => v.group === label);
};
